// A single-use, tab-local handoff. Scripts never enter cookies, URLs or analytics.
const PREFIX = 'audiocrowd.voiceover-draft.';
const TTL = 30 * 60 * 1000;
const MAX_BYTES = 600000;
const TOKEN = /^[a-f0-9-]{36}$/;
const FIELD = /^human_voiceover_request\[(requested_speaker_id|project_title|project_description|audience|tone|delivery_deadline|output_format|music_requested|additional_notes|segments|usage_answers)\]/;

function fields(form) {
  return Array.from(form.querySelectorAll('input[name], textarea[name], select[name]')).filter((field) => FIELD.test(field.name));
}

function purgeExpired(storage) {
  for (const key of Object.keys(storage)) {
    if (!key.startsWith(PREFIX)) continue;
    try {
      const draft = JSON.parse(storage.getItem(key));
      if (!draft || draft.expiresAt <= Date.now()) storage.removeItem(key);
    } catch (_) {
      storage.removeItem(key);
    }
  }
}

export function saveVoiceoverDraft(form, returnPath, storage = window.sessionStorage) {
  purgeExpired(storage);
  const token = TOKEN.test(form.dataset.voiceoverDraftToken || '') ? form.dataset.voiceoverDraftToken : window.crypto.randomUUID();
  const entries = fields(form).filter((field) => !['radio', 'checkbox'].includes(field.type) || field.checked).map((field) => [field.name, field.value]);
  const payload = JSON.stringify({ expiresAt: Date.now() + TTL, entries });
  if (payload.length > MAX_BYTES) throw new Error('Draft too large');
  storage.setItem(PREFIX + token, payload);
  form.dataset.voiceoverDraftToken = token;
  const url = new URL(returnPath, window.location.origin);
  url.searchParams.set('draft', token);
  return url.pathname + url.search + url.hash;
}

export function restoreVoiceoverDraft(form, url = new URL(window.location.href), storage = window.sessionStorage) {
  if (form.dataset.voiceoverAuthenticated !== 'true' || form.dataset.voiceoverRestoreDraft === 'false') return false;
  const token = url.searchParams.get('draft');
  if (!token || !TOKEN.test(token)) return false;
  const key = PREFIX + token;
  const raw = storage.getItem(key);
  const discard = () => { storage.removeItem(key); return false; };
  if (!raw || raw.length > MAX_BYTES) return discard();
  let draft;
  try { draft = JSON.parse(raw); } catch (_) { return discard(); }
  if (!draft || !Number.isFinite(draft.expiresAt) || draft.expiresAt <= Date.now() || !Array.isArray(draft.entries)) return discard();
  const entries = draft.entries.filter((entry) => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string' && typeof entry[1] === 'string' && FIELD.test(entry[0]));
  const container = form.querySelector('[data-voiceover-segments]');
  const template = form.querySelector('[data-voiceover-segment-template]');
  const maxSegments = Math.min(Number(form.dataset.voiceoverMaxSegments) || 20, 20);
  const segmentIndexes = entries.map(([name]) => name.match(/\[segments\]\[(\d+)\]/)).filter(Boolean).map((match) => Number(match[1]));
  const count = Math.min(Math.max(1, ...segmentIndexes.map((index) => index + 1)), maxSegments);
  if (container && template) {
    while (container.querySelectorAll('[data-voiceover-segment]').length < count) {
      const index = container.querySelectorAll('[data-voiceover-segment]').length;
      container.insertAdjacentHTML('beforeend', template.innerHTML.replace(/__INDEX__/g, String(index)).replace(/__NUMBER__/g, String(index + 1)));
    }
  }
  fields(form).forEach((field) => {
    const values = entries.filter(([name]) => name === field.name).map(([, value]) => value);
    if (['radio', 'checkbox'].includes(field.type)) field.checked = values.includes(field.value);
    else if (values.length) field.value = values[0].slice(0, field.maxLength > 0 ? field.maxLength : 20000);
  });
  storage.removeItem(key);
  return true;
}
